import {
    useRef,
    UIEvent,
    useLayoutEffect,
    useCallback,
    MutableRefObject,
    useEffect,
    createContext,
    ReactNode,
    useContext
} from "react";
import { useLocation } from "react-router-dom";
import { usePreload } from "./PreloadProvider";
import { useDebounce } from "@/frontend/@tools/useDebounce";

// stop browser from trying to restore scroll position
window.history.scrollRestoration = "manual";

const RESTORE_DELAY = 100;
const SCROLL_DEBOUNCE = 250;
const ACTIVATION_DELAY = 100;

interface Context {
    getState: (containerId: string) => State;
    saveScroll: (
        containerId: string,
        historyKey: string,
        scrollTop: number,
        scrollLeft: number
    ) => void;
}

interface State {
    containerId: string;
    storedScrolls: Record<string, ScrollState>;
    currentPageUrl: string | undefined;
}

interface ScrollState {
    scrollTop: number;
    scrollLeft: number;
}

type HistoryState = { key?: string } | null;

const ScrollRestorationContext = createContext<Context>({
    getState: () => ({
        containerId: "",
        storedScrolls: {},
        currentPageUrl: undefined,
        intentionalNaviationUrl: undefined
    }),
    saveScroll: () => undefined
});

export const ScrollRestorationProvider = ({
    children
}: {
    children: ReactNode;
}) => {
    const pageState = useRef<State[]>([]);

    const getState = (containerId: string) => {
        const existingState = pageState.current.find(
            x => x.containerId === containerId
        );
        if (existingState) {
            return existingState;
        }

        let storedScrolls: Record<string, ScrollState> = {};

        const savedString = sessionStorage.getItem(getStorageKey(containerId));
        if (savedString) {
            storedScrolls = JSON.parse(savedString) as Record<
                string,
                ScrollState
            >;
        }

        const state: State = {
            containerId: containerId,
            storedScrolls,
            currentPageUrl: undefined
        };
        pageState.current.push(state);

        return state;
    };

    const saveScroll = (
        containerId: string,
        historyKey: string,
        scrollTop: number,
        scrollLeft: number
    ) => {
        const state = getState(containerId);
        state.storedScrolls[historyKey] = { scrollTop, scrollLeft };
        sessionStorage.setItem(
            getStorageKey(containerId),
            JSON.stringify(state.storedScrolls)
        );
    };

    return (
        <ScrollRestorationContext.Provider value={{ getState, saveScroll }}>
            {children}
        </ScrollRestorationContext.Provider>
    );
};

const getStorageKey = (containerId: string) => {
    return `scrollRestorationState-${containerId}`;
};

const getHistoryKey = (url: string) => {
    const history = window.history.state as HistoryState;
    return history?.key ?? url;
};

export const useScrollRestoration = ({
    containerId,
    root
}: {
    containerId: string;
    root?: boolean;
}) => {
    const { isPreloading } = usePreload();

    const { pathname } = useLocation();
    const url = pathname;

    const { getState, saveScroll } = useContext(ScrollRestorationContext);
    const state = getState(containerId);

    const elementRef: MutableRefObject<HTMLElement | null> = useRef(null);
    const setAgainTimeoutRef = useRef<NodeJS.Timeout | undefined>();
    const pageChangedTimeRef = useRef(0);

    // set ref to root element if needed
    useLayoutEffect(() => {
        if (isPreloading) {
            return;
        }
        if (root) {
            elementRef.current = document.documentElement;
        }
    }, [root, isPreloading]);

    // track when the page changes
    useLayoutEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        url; // force url to be a dependency
        pageChangedTimeRef.current = Date.now();
    }, [url]);

    // update scroll positiion with debounce
    const updateScroll = (element: HTMLElement, historyKey: string) => {
        const { scrollTop, scrollLeft } = element;
        saveScroll(containerId, historyKey, scrollTop, scrollLeft);
    };
    const debouncedUpdateScroll = useDebounce(updateScroll, SCROLL_DEBOUNCE);

    // scroll handler
    const onScroll = useCallback(
        (e: UIEvent<HTMLElement>) => {
            if (isPreloading) {
                return;
            }
            // weird issue: getting a 0 position scroll event immediately after a page change, causing a saved position to be removed
            // this fix ignores scroll events that happen too soon after a page change
            if (Date.now() - pageChangedTimeRef.current < ACTIVATION_DELAY) {
                return;
            }
            debouncedUpdateScroll(e.target as HTMLElement, getHistoryKey(url));
        },
        [debouncedUpdateScroll, isPreloading, url]
    );

    // put scroll handler on the root element if needed
    useEffect(() => {
        if (isPreloading) {
            return;
        }

        if (root) {
            const rootScroll = () => {
                onScroll({
                    target: document.documentElement
                } as unknown as UIEvent<HTMLElement>);
            };
            document.addEventListener("scroll", rootScroll);
            return () => {
                document.removeEventListener("scroll", rootScroll);
            };
        }
    }, [root, onScroll, isPreloading]);

    // restore scroll
    useLayoutEffect(() => {
        if (isPreloading) {
            return;
        }

        // check if the current page has changed
        if (url === state.currentPageUrl) {
            return;
        }
        state.currentPageUrl = url;

        const historyKey = getHistoryKey(url);

        // check if scroll state exists. if it doesn't, it means the page has not been navigated to yet and should be scrolled to the top
        let scroll: ScrollState | undefined = state.storedScrolls[historyKey];
        if (!scroll) {
            saveScroll(containerId, historyKey, 0, 0);
            scroll = { scrollTop: 0, scrollLeft: 0 };
        }

        // check if the element exists
        if (!elementRef.current) {
            return;
        }

        // restore scroll state
        const element = elementRef.current;
        element.scrollTo(scroll.scrollLeft, scroll.scrollTop);

        // restore again with a delay to help ensure that scroll is restored after the page has been updated
        // NOTE: since setting window.history.scrollRestoration = "manual", this may not be needed. but it isn't causing any harm so leaving it in
        if (setAgainTimeoutRef.current) {
            clearTimeout(setAgainTimeoutRef.current);
        }
        setAgainTimeoutRef.current = setTimeout(() => {
            if (element === elementRef.current) {
                element.scrollTo(scroll.scrollLeft, scroll.scrollTop);
            }
            clearTimeout(setAgainTimeoutRef.current);
        }, RESTORE_DELAY);
    }, [containerId, url, isPreloading, saveScroll, state]);

    return {
        ref: elementRef,
        onScroll
    };
};
